import { Controller } from "@hotwired/stimulus"

// Unified bulk selection controller for both tasks_beta and subtasks contexts
export default class extends Controller {
  static targets = ["checkbox", "selectAll", "selectAllLabel", "filterCheck", "taskTypeFilter",
                    "expandCollapseBtn", "expandCollapseIcon", "expandCollapseLabel"]
  static values = {
    context: String,        // 'tasks_beta' or 'subtasks'
    taskId: String,         // Parent task ID (subtasks context only)
    subtasksDisplay: String // Subtasks display mode (tasks_beta context only)
  }

  connect() {
    this.selectedIds = new Set()
    this.currentFilter = 'all'
    this.hasMixedSelection = false
    this.allExpanded = false
    this.lastClickedCheckbox = null // Track last clicked checkbox for shift+click

    // Get initial filter value from dropdown if exists
    if (this.hasTaskTypeFilterTarget) {
      this.currentFilter = this.taskTypeFilterTarget.value
    }

    // Store original label text
    if (this.hasSelectAllLabelTarget) {
      this.originalLabelText = this.selectAllLabelTarget.textContent
    }

    this.updateBulkFormVisibility()

    // Listen for form submissions
    this.boundHandleFormSubmit = this.handleFormSubmit.bind(this)
    document.addEventListener('turbo:submit-end', this.boundHandleFormSubmit)

    // Listen for escape key
    this.boundHandleEscapeKey = this.handleEscapeKey.bind(this)
    document.addEventListener('keydown', this.boundHandleEscapeKey, true)

    // Subtasks context: listen for offcanvas close
    if (this.isSubtasks) {
      this.boundHandleOffcanvasClose = this.handleOffcanvasClose.bind(this)
      const offcanvas = this.element.closest('.offcanvas')
      if (offcanvas) {
        offcanvas.addEventListener('hidden.bs.offcanvas', this.boundHandleOffcanvasClose)
        this.offcanvasElement = offcanvas
      }
    }
  }

  disconnect() {
    if (this.boundHandleFormSubmit) {
      document.removeEventListener('turbo:submit-end', this.boundHandleFormSubmit)
    }
    if (this.boundHandleEscapeKey) {
      document.removeEventListener('keydown', this.boundHandleEscapeKey, true)
    }
    if (this.boundHandleOffcanvasClose && this.offcanvasElement) {
      this.offcanvasElement.removeEventListener('hidden.bs.offcanvas', this.boundHandleOffcanvasClose)
    }
  }

  // Context helpers
  get isSubtasks() {
    return this.contextValue === 'subtasks'
  }

  get isTasksBeta() {
    return this.contextValue === 'tasks_beta'
  }

  get frameId() {
    return this.isSubtasks ? 'subtasks-bulk-form-frame' : 'tasks-beta-bulk-form-frame'
  }

  get panelId() {
    return this.isSubtasks ? 'subtasks-bulk-panel' : 'tasks-beta-bulk-panel'
  }

  get updateFormId() {
    return this.isSubtasks ? 'subtasks-bulk-update-form' : 'tasks-beta-bulk-update-form'
  }

  get deleteFormId() {
    return this.isSubtasks ? 'subtasks-bulk-delete-form' : 'tasks-beta-bulk-delete-form'
  }

  getItemId(checkbox) {
    return checkbox.dataset.taskId || checkbox.dataset.subtaskId
  }

  // Toggle individual checkbox (supports shift+click for range selection)
  toggleCheckbox(event) {
    const checkbox = event.currentTarget
    const itemId = this.getItemId(checkbox)
    const taskType = checkbox.dataset.taskType
    const isShiftClick = event.shiftKey

    // Handle shift+click for range selection
    if (isShiftClick && this.lastClickedCheckbox && this.lastClickedCheckbox !== checkbox) {
      this.handleShiftClick(checkbox)
      this.lastClickedCheckbox = checkbox
      this.updateBulkFormVisibility()
      this.updateSelectAllCheckbox()
      return
    }

    // Store as last clicked checkbox for future shift+click
    this.lastClickedCheckbox = checkbox

    if (checkbox.checked) {
      this.selectedIds.add(itemId)

      // Check for mixed selection
      if (this.currentFilter !== 'all' && taskType !== this.currentFilter) {
        this.currentFilter = 'all'
        this.hasMixedSelection = true
        if (this.hasTaskTypeFilterTarget) {
          this.taskTypeFilterTarget.value = 'all'
        }
      }
      this.updateMixedSelectionState()
    } else {
      this.selectedIds.delete(itemId)
      this.updateMixedSelectionState()

      if (this.selectedIds.size === 0) {
        this.hasMixedSelection = false
      }
    }

    this.updateBulkFormVisibility()
    this.updateSelectAllCheckbox()
  }

  // Handle shift+click to select/deselect range of checkboxes
  handleShiftClick(currentCheckbox) {
    const allCheckboxes = this.checkboxTargets.filter(cb => this.isCheckboxVisible(cb))
    const lastIndex = allCheckboxes.indexOf(this.lastClickedCheckbox)
    const currentIndex = allCheckboxes.indexOf(currentCheckbox)

    if (lastIndex === -1 || currentIndex === -1) return

    // Determine the range (works regardless of click order)
    const startIndex = Math.min(lastIndex, currentIndex)
    const endIndex = Math.max(lastIndex, currentIndex)

    // Use the state of the current checkbox (after the click) to determine action
    const shouldCheck = currentCheckbox.checked

    // Apply the same state to all checkboxes in the range
    for (let i = startIndex; i <= endIndex; i++) {
      const cb = allCheckboxes[i]
      cb.checked = shouldCheck
      const cbItemId = this.getItemId(cb)

      if (shouldCheck) {
        this.selectedIds.add(cbItemId)
      } else {
        this.selectedIds.delete(cbItemId)
      }
    }

    this.updateMixedSelectionState()
  }

  // Check if selection contains both client and employee tasks
  updateMixedSelectionState() {
    if (this.selectedIds.size === 0) {
      this.hasMixedSelection = false
      return
    }

    let hasClient = false
    let hasInternal = false

    for (const checkbox of this.checkboxTargets) {
      if (this.selectedIds.has(this.getItemId(checkbox))) {
        if (checkbox.dataset.taskType === 'client') {
          hasClient = true
        } else {
          hasInternal = true
        }
        if (hasClient && hasInternal) {
          this.hasMixedSelection = true
          return
        }
      }
    }

    this.hasMixedSelection = hasClient && hasInternal

    // Update filter if all selections are same type
    if (!this.hasMixedSelection && this.selectedIds.size > 0) {
      const newFilter = hasClient ? 'client' : 'employee'
      if (this.currentFilter !== newFilter) {
        this.currentFilter = newFilter
        if (this.hasTaskTypeFilterTarget) {
          this.taskTypeFilterTarget.value = newFilter
        }
      }
    }
  }

  // Select/deselect all visible checkboxes
  toggleAll(event) {
    const selectAllChecked = event.currentTarget.checked

    if (selectAllChecked) {
      this.checkboxTargets.forEach(checkbox => {
        if (this.isCheckboxVisible(checkbox) && this.matchesFilter(checkbox)) {
          checkbox.checked = true
          this.selectedIds.add(this.getItemId(checkbox))
        }
      })
      this.updateMixedSelectionState()
    } else {
      this.checkboxTargets.forEach(checkbox => {
        checkbox.checked = false
      })
      this.selectedIds.clear()
      this.hasMixedSelection = false
    }

    this.updateBulkFormVisibility()
  }

  // Select/deselect all checkboxes within a specific container (subtasks nested views)
  toggleAllInContainer(event) {
    const selectAllChecked = event.currentTarget.checked
    const containerId = event.currentTarget.dataset.containerId

    const container = document.querySelector(`[data-subtask-container="${containerId}"]`)
    if (!container) return

    const allCheckboxes = container.querySelectorAll('input[data-bulk-selection-target="checkbox"]')

    // Filter to direct children only
    const directCheckboxes = Array.from(allCheckboxes).filter(checkbox => {
      let parent = checkbox.parentElement
      while (parent && parent !== container) {
        if (parent.hasAttribute('data-subtask-container') && parent !== container) {
          return false
        }
        parent = parent.parentElement
      }
      return true
    })

    if (selectAllChecked) {
      directCheckboxes.forEach(checkbox => {
        if (this.isCheckboxVisible(checkbox) && this.matchesFilter(checkbox)) {
          checkbox.checked = true
          this.selectedIds.add(this.getItemId(checkbox))
        }
      })
      this.updateMixedSelectionState()
    } else {
      directCheckboxes.forEach(checkbox => {
        checkbox.checked = false
        this.selectedIds.delete(this.getItemId(checkbox))
      })
      this.updateMixedSelectionState()
    }

    this.updateBulkFormVisibility()
  }

  // Select tasks by filter type (from dropdown)
  selectByFilter(event) {
    event.preventDefault()
    const filter = event.currentTarget.dataset.filter
    this.currentFilter = filter
    this.hasMixedSelection = false

    this.updateFilterCheckmarks()

    if (this.hasTaskTypeFilterTarget) {
      this.taskTypeFilterTarget.value = filter
    }

    // Clear and reselect
    this.checkboxTargets.forEach(checkbox => {
      checkbox.checked = false
    })
    this.selectedIds.clear()

    this.checkboxTargets.forEach(checkbox => {
      if (this.isCheckboxVisible(checkbox) && this.matchesFilter(checkbox)) {
        checkbox.checked = true
        this.selectedIds.add(this.getItemId(checkbox))
      }
    })

    this.updateMixedSelectionState()
    this.updateSelectAllCheckbox()
    this.updateBulkFormVisibility()
  }

  // Check if checkbox matches current filter
  matchesFilter(checkbox) {
    if (this.currentFilter === 'all') return true
    return checkbox.dataset.taskType === this.currentFilter
  }

  // Update checkmark visibility in filter dropdown
  updateFilterCheckmarks() {
    this.filterCheckTargets.forEach(check => {
      if (check.dataset.filter === this.currentFilter) {
        check.classList.remove('invisible')
      } else {
        check.classList.add('invisible')
      }
    })
  }

  // Get info about selected tasks
  getSelectedTaskInfo() {
    let isClientTask = false
    let accountId = null
    let shouldDisableAssignee = false

    for (const checkbox of this.checkboxTargets) {
      if (this.selectedIds.has(this.getItemId(checkbox))) {
        isClientTask = checkbox.dataset.taskType === 'client'
        accountId = checkbox.dataset.accountId
        break
      }
    }

    shouldDisableAssignee = this.currentFilter === 'all' || this.hasMixedSelection

    return { isClientTask, accountId, shouldDisableAssignee }
  }

  // Update the visibility and content of bulk form
  updateBulkFormVisibility() {
    const hasSelection = this.selectedIds.size > 0
    const frame = document.getElementById(this.frameId)
    const panel = document.getElementById(this.panelId)

    if (!frame || !panel) return

    if (hasSelection) {
      // Add padding class for subtasks context
      if (this.isSubtasks) {
        const offcanvasBody = this.element.closest('.offcanvas-body')
        const modalBody = this.element.closest('.modal-body')
        if (offcanvasBody) offcanvasBody.classList.add('has-bulk-form')
        if (modalBody) modalBody.classList.add('has-bulk-form')
      }

      const url = this.buildBulkFormUrl()
      frame.src = url

      // Open panel
      const turboPanelController = this.application.getControllerForElementAndIdentifier(
        this.element, 'turbo-panel'
      )
      if (turboPanelController) {
        turboPanelController.openPanel(`#${this.panelId}`, this.panelId)
      }
    } else {
      // Close panel
      const turboPanelController = this.application.getControllerForElementAndIdentifier(
        this.element, 'turbo-panel'
      )
      if (turboPanelController) {
        turboPanelController.closeSpecificPanel(this.panelId)
      }

      frame.removeAttribute('src')
      frame.innerHTML = ''

      // Remove padding class for subtasks context
      if (this.isSubtasks) {
        const offcanvasBody = this.element.closest('.offcanvas-body')
        const modalBody = this.element.closest('.modal-body')
        if (offcanvasBody) offcanvasBody.classList.remove('has-bulk-form')
        if (modalBody) modalBody.classList.remove('has-bulk-form')
      }
    }

    // Update select all label with count
    if (this.hasSelectAllLabelTarget && this.originalLabelText) {
      if (this.selectedIds.size > 0) {
        this.selectAllLabelTarget.textContent = `${this.originalLabelText} (${this.selectedIds.size} selected)`
      } else {
        this.selectAllLabelTarget.textContent = this.originalLabelText
      }
    }
  }

  buildBulkFormUrl() {
    const taskInfo = this.getSelectedTaskInfo()
    const selectedCheckboxes = this.checkboxTargets.filter(cb => this.selectedIds.has(this.getItemId(cb)))
    const taskTypes = [...new Set(selectedCheckboxes.map(cb => cb.dataset.taskType))]
    const accountIds = [...new Set(selectedCheckboxes.map(cb => cb.dataset.accountId).filter(Boolean))]
    const taskableTypes = [...new Set(selectedCheckboxes.map(cb => cb.dataset.taskableType).filter(Boolean))]

    if (this.isSubtasks) {
      const idsParam = Array.from(this.selectedIds).map(id => `subtask_ids[]=${id}`).join('&')
      const taskTypeParam = taskInfo.isClientTask ? '&task_type=client' : '&task_type=employee'
      const accountIdParam = taskInfo.accountId ? `&account_id=${taskInfo.accountId}` : ''
      const disableAssigneeParam = taskInfo.shouldDisableAssignee ? '&disable_assignee=true' : ''
      const taskableTypesParam = taskableTypes.map(t => `taskable_types[]=${encodeURIComponent(t)}`).join('&')
      return `/tasks/${this.taskIdValue}/subtasks/bulk/new?${idsParam}${taskTypeParam}${accountIdParam}${disableAssigneeParam}&${taskableTypesParam}`
    } else {
      const idsParam = Array.from(this.selectedIds).map(id => `task_ids[]=${id}`).join('&')
      let url = `/tasks/beta/bulk/new?${idsParam}&filter=${this.currentFilter}`

      if (taskTypes.length === 1) {
        url += `&task_type=${taskTypes[0]}`
      }
      if (accountIds.length === 1) {
        url += `&account_id=${accountIds[0]}`
      } else if (accountIds.length > 1) {
        url += '&mixed_accounts=true'
      }

      // Pass taskable_types for status filtering
      taskableTypes.forEach(t => {
        url += `&taskable_types[]=${encodeURIComponent(t)}`
      })

      // Pass view_id from URL
      const currentParams = new URLSearchParams(window.location.search)
      const viewId = currentParams.get('view_id')
      if (viewId) {
        url += `&view_id=${viewId}`
      }

      // Pass subtasks_display from controller value
      if (this.subtasksDisplayValue) {
        url += `&subtasks_display=${this.subtasksDisplayValue}`
      }

      return url
    }
  }

  // Update select all checkbox state
  updateSelectAllCheckbox() {
    if (!this.hasSelectAllTarget) return

    const relevantCheckboxes = this.checkboxTargets.filter(cb =>
      this.isCheckboxVisible(cb) && this.matchesFilter(cb)
    )
    const checkedCheckboxes = relevantCheckboxes.filter(cb => cb.checked)

    if (checkedCheckboxes.length === 0) {
      this.selectAllTarget.checked = false
      this.selectAllTarget.indeterminate = false
    } else if (checkedCheckboxes.length === relevantCheckboxes.length && relevantCheckboxes.length > 0) {
      this.selectAllTarget.checked = true
      this.selectAllTarget.indeterminate = false
    } else {
      this.selectAllTarget.checked = false
      this.selectAllTarget.indeterminate = true
    }
  }

  // Clear selection
  clearSelection() {
    this.selectedIds.clear()
    this.hasMixedSelection = false
    this.lastClickedCheckbox = null

    this.checkboxTargets.forEach(checkbox => {
      checkbox.checked = false
    })

    if (this.hasSelectAllTarget) {
      this.selectAllTarget.checked = false
      this.selectAllTarget.indeterminate = false
    }

    if (this.hasSelectAllLabelTarget && this.originalLabelText) {
      this.selectAllLabelTarget.textContent = this.originalLabelText
    }

    this.currentFilter = 'all'
    this.updateFilterCheckmarks()

    if (this.hasTaskTypeFilterTarget) {
      this.taskTypeFilterTarget.value = 'all'
    }

    this.updateBulkFormVisibility()
  }

  // Handle form submit
  handleFormSubmit(event) {
    const form = event.target
    if (form.id !== this.updateFormId && form.id !== this.deleteFormId) {
      return
    }

    const response = event.detail.fetchResponse
    if (response && response.succeeded) {
      this.clearSelection()
    }
  }

  // Handle escape key
  handleEscapeKey(event) {
    if (event.key !== 'Escape') return

    if (this.selectedIds.size > 0) {
      event.stopPropagation()
      event.preventDefault()
      this.clearSelection()
    }
  }

  // Handle offcanvas close (subtasks only)
  handleOffcanvasClose() {
    this.clearSelection()
  }

  // Toggle expand/collapse all (subtasks only)
  toggleExpandCollapse() {
    const collapseButtons = document.querySelectorAll('[data-turbo-collapse-target="collapser"]')

    if (this.allExpanded) {
      collapseButtons.forEach(button => {
        const content = button.closest('[data-controller="turbo-collapse"]')?.querySelector('[data-turbo-collapse-target="content"]')
        if (content && !content.classList.contains('d-none')) {
          button.click()
        }
      })
      this.allExpanded = false
      if (this.hasExpandCollapseLabelTarget) {
        this.expandCollapseLabelTarget.textContent = 'Expand all'
      }
      if (this.hasExpandCollapseIconTarget) {
        this.expandCollapseIconTarget.style.transform = 'rotate(0deg)'
      }
    } else {
      collapseButtons.forEach(button => {
        const content = button.closest('[data-controller="turbo-collapse"]')?.querySelector('[data-turbo-collapse-target="content"]')
        if (content && content.classList.contains('d-none')) {
          button.click()
        }
      })
      this.allExpanded = true
      if (this.hasExpandCollapseLabelTarget) {
        this.expandCollapseLabelTarget.textContent = 'Collapse all'
      }
      if (this.hasExpandCollapseIconTarget) {
        this.expandCollapseIconTarget.style.transform = 'rotate(90deg)'
      }
    }
  }

  // Filter task type display (subtasks only)
  filterTaskTypeDisplay(event) {
    const filterType = event.currentTarget.value
    const allSubtasks = document.querySelectorAll('[data-subtask-row]')

    allSubtasks.forEach(subtask => {
      const taskType = subtask.dataset.taskType
      const wrapper = subtask.closest('[data-controller*="turbo-collapse"]')
      const targetElement = wrapper || subtask

      if (filterType === 'all') {
        targetElement.style.display = ''
      } else if (filterType === taskType) {
        targetElement.style.display = ''
      } else {
        targetElement.style.display = 'none'
      }
    })
  }

  // Helper: check if checkbox is visible
  isCheckboxVisible(checkbox) {
    if (!checkbox.offsetParent) return false

    let element = checkbox.parentElement
    while (element && element !== document.body) {
      if (element.classList.contains('d-none')) return false
      if (window.getComputedStyle(element).display === 'none') return false
      element = element.parentElement
    }

    return true
  }
}
